/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import React, { ChangeEvent, useState } from "react";
import { useQueryListContext } from "./QueryListContext";
import { Search, X } from "lucide-react";

interface ListSearch {
    search: Map<string, string>;
    setSearch: React.Dispatch<React.SetStateAction<Map<string, string>>>;
    searchClicked: () => void;
    clearClicked: () => void;
    searchableColumns?: { name: string; type: string; sortable: boolean; searchable: boolean; }[] | null;
}

export const ListSearch: React.FC<ListSearch> = (props) => {

    const { ctxData, getData, setData } = useQueryListContext();
    const [activeStatusKey, setActiveStatusKey] = useState<string | null>(null);

    const safeLower = (v: unknown): string =>
        typeof v === "string" ? v.toLowerCase() : "";

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.type === "text") {
            props.setSearch((prevSearch) => {
                const newSearch = new Map(prevSearch);
                newSearch.set(event.target.id, event.target.value.trim());
                return newSearch;
            });
        }
    };

    const columns: React.ReactElement[] = [];
    let i = 0;

    const thStyle: React.CSSProperties = {
        border: "1px solid #deebed",
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap",
        backgroundColor: "#f8f9fa",
    };

    const transformString = (inputString: string) => {
        if (typeof inputString !== "string") return inputString;

        const result = inputString.split("_").map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

        return result.charAt(0).toUpperCase() + result.slice(1);
    };

    props.search.forEach((value: string, name: string) => {
        const lower = safeLower(name);
        const placeholderName = transformString(name);

        // Skip fields that should never have search boxes
        // if (lower === "notifications.reference") return;
        // if (lower === "id") return;

        // // Special case for assets.url (no input)
        // if (lower === "assets.url") {
        //     columns.push(<th key={i++} scope="col" style={thStyle} className="sa-search-control"></th>);
        //     return;
        // }

        // Determine if this column is searchable based on metadata
        const colMeta = props.searchableColumns?.find((col) => safeLower(col.name) === lower);

        const isSearchable = colMeta?.searchable ?? false;

        columns.push(
            <th key={i++} scope="col" style={thStyle} className="sa-search-control">
                <input id={name} name={name} onChange={handleOnChange} value={value} type="text" autoComplete="off"
                    placeholder={isSearchable ? `Search ${placeholderName}...` : `${placeholderName} (not searchable)`}
                    className="form-control form-control-md"
                    disabled={!isSearchable}
                />
            </th>
        );
    });

    return (
        <>
            <thead className="sa-list-search">
                <tr id="searchBar">
                    {columns}

                    {/* Control buttons */}
                    <th style={thStyle}>
                        <div className="d-flex justify-content-end gap-1 w-100">
                            <button type="button" className="btn btn-outline-danger btn-md d-flex justify-content-center align-items-center" onClick={props.searchClicked}>
                                <Search style={{ width: "21px", height: "21px" }} strokeWidth={2} />
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-md d-flex justify-content-center align-items-center" onClick={props.clearClicked}>
                                <X style={{ width: "21px", height: "21px" }} strokeWidth={2} />
                            </button>
                        </div>
                    </th>
                </tr>
            </thead>
        </>
    );
};



